import { AlignLeft, Menu } from 'lucide-react';

import { SettingsSearch, SettingsSearchTrigger } from '@/components/SettingsSearch';
import { TopBar } from '@/components/TopBar/TopBar';
import { UserMenu } from '@/components/UserMenu/UserMenu';
import { WorkspaceSwitcher } from '@/components/WorkspaceSwitcher/WorkspaceSwitcher';
import { useT } from '@/core/providers/I18nProvider';

import { BrandMark } from './BrandMark';

import type { SidebarItem } from '@/components/Sidebar/Sidebar';
import type { ControlMode } from '@/core/providers/ControlModeProvider';
import type { Workspace } from '@/core/providers/WorkspaceProvider';
import type { ReactElement, RefObject } from 'react';

/**
 * The dashboard's top bar, composed.
 *
 * Split out of `AppShell` when the settings search arrived and took the shell
 * past the ~300-line ceiling (`.claude/rules/architecture.md`). A real seam
 * rather than a line-count trim: everything here decides what the BAR shows,
 * and everything left in the shell decides what the PAGE region does. The bar
 * owns no state — every value and every callback is passed in, so the shell is
 * still the one place that knows whether the viewport is mobile or the sidebar
 * is collapsed.
 *
 * Three slots, and the mobile split runs through two of them: below `md` the bar
 * already carries the drawer button, the brand, the workspace switcher and the
 * user menu, so the search is an icon in `end` that opens the palette dialog
 * instead of a field in `center`.
 */

const ICON_BUTTON_CLASS =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-hairline bg-surface text-foreground transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

export interface ShellTopBarProps {
  readonly mobile: boolean;
  readonly collapsed: boolean;
  readonly drawerOpen: boolean;
  /** The drawer restores focus here when it closes. */
  readonly drawerTriggerRef: RefObject<HTMLButtonElement>;
  readonly onToggleDrawer: () => void;
  readonly onToggleCollapse: () => void;
  /** The nav rows, which the settings search reads for its breadcrumbs. */
  readonly sidebarItems: readonly SidebarItem[];
  readonly onOpenPalette: () => void;
  readonly workspaces: readonly Workspace[];
  readonly currentWorkspace: Workspace | null;
  readonly onSelectWorkspace: (id: string) => void;
  readonly onCreateWorkspace: () => void;
  readonly onAccount: () => void;
  readonly user: { readonly name: string; readonly email: string; readonly picture: string | null };
  readonly theme: 'light' | 'dark';
  readonly onToggleTheme: () => void;
  readonly controlMode: ControlMode;
  readonly onToggleControlMode: () => void;
  readonly onSignOut: () => void;
}

export function ShellTopBar({
  mobile,
  collapsed,
  drawerOpen,
  drawerTriggerRef,
  onToggleDrawer,
  onToggleCollapse,
  sidebarItems,
  onOpenPalette,
  workspaces,
  currentWorkspace,
  onSelectWorkspace,
  onCreateWorkspace,
  onAccount,
  user,
  theme,
  onToggleTheme,
  controlMode,
  onToggleControlMode,
  onSignOut,
}: ShellTopBarProps): ReactElement {
  const { t } = useT();
  return (
    <TopBar
      start={
        // `min-w-0` so the workspace switcher can shrink + truncate within the
        // start slot rather than pushing the bar past the viewport on mobile.
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          {mobile ? (
            <button
              ref={drawerTriggerRef}
              type="button"
              aria-label={drawerOpen ? t('appshell.closeMenu') : t('appshell.openMenu')}
              aria-expanded={drawerOpen}
              aria-controls="app-shell-drawer"
              onClick={onToggleDrawer}
              className={ICON_BUTTON_CLASS}
            >
              <Menu aria-hidden="true" className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              aria-label={collapsed ? t('appshell.expandSidebar') : t('appshell.collapseSidebar')}
              aria-expanded={!collapsed}
              onClick={onToggleCollapse}
              className={ICON_BUTTON_CLASS}
            >
              <AlignLeft aria-hidden="true" className="h-4 w-4" />
            </button>
          )}
          {/* SYN-31 brand cluster: the logo glyph (with glow) + the gradient
            "Syncanix" wordmark. The glyph stays as the brand presence on a
            narrow bar; the wordmark joins it from `sm` up. */}
          <BrandMark />
          {/* Hairline seam between the product zone (brand) and the context
            zone (workspace) — mirrors the Vercel-style logo / divider /
            team-switcher grouping. Hidden below sm, where every pixel of
            bar width goes to the switcher. */}
          <span aria-hidden="true" className="hidden h-5 w-px shrink-0 bg-hairline sm:block" />
          <WorkspaceSwitcher
            workspaces={workspaces}
            current={currentWorkspace}
            onSelect={onSelectWorkspace}
            onCreateWorkspace={onCreateWorkspace}
          />
        </div>
      }
      // Desktop only: below `md` the bar is already carrying four things and a
      // field would push one off it. The phone gets the icon in `end`, which
      // opens the same index in the shape a phone can hold.
      {...(mobile ? {} : { center: <SettingsSearch sidebarItems={sidebarItems} /> })}
      end={
        // Tighter gap on narrow bars, mirroring the start slot.
        <div className="flex items-center gap-2 sm:gap-3">
          {mobile ? <SettingsSearchTrigger onOpen={onOpenPalette} /> : null}
          <UserMenu
            user={user}
            theme={theme}
            onToggleTheme={onToggleTheme}
            controlMode={controlMode}
            onToggleControlMode={onToggleControlMode}
            onAccount={onAccount}
            onSignOut={onSignOut}
          />
        </div>
      }
    />
  );
}
